import type { AllergenOption } from "@/components/ActiveProjects/AllergensAndSensitivities/types";

// Static list of allergen declaration options
export const ALLERGEN_DECLARATION_TEXT_LIST = [
  "Artwork Team - Remove contains statement for this item (variance approved)",
  "Artwork Team - Replace \"Other Fish\" with {}",
  "Artwork Team - Replace \"Other Shellfish (crustacean)\" with {}",
  "Artwork Team - Replace \"Other Tree Nuts\" with {}",
  "Artwork Team - Replace {} with {}",
  "Artwork Team Add - May Contain {}",
  "This product is made with Goat's Milk",
  "This product is made with Sheep's Milk",
  "This product is made with {}"
];

// Type for API allergen declaration (re-exported from main types for convenience)
export type ApiAllergenDeclaration = AllergenOption;

/**
 * Transform API allergen declarations to the format expected by the application
 * @param apiDeclarations - Array of allergen declarations from the API
 * @returns Transformed allergen declaration options with display formatting
 */
export const transformApiAllergenDeclarations = (apiDeclarations: AllergenOption[]) => {
  if (!apiDeclarations || !Array.isArray(apiDeclarations)) {
    return [];
  }

  return apiDeclarations?.filter(item => item && item?.name).map((item) => {
    // API uses {l} as placeholder, normalize to {} for consistency
    const text = item.name?.replace(/\{l\}/g, '{}') || '';
    
    // Determine input type based on text content
    let inputType = 'single';
    let displayLabel = text;
    let secondLabel = '';

    if (text?.includes('{} with {}')) {
      inputType = 'double';
      displayLabel = text?.replace(' {} with {}', '') || text;
      secondLabel = 'With';
    } else if (text?.includes('{}')) {
      inputType = 'single';
      displayLabel = text?.replace(' {}', '') || text;
    } else if (text?.includes('variance approved') || !text?.includes('{}')) {
      inputType = 'none';
      displayLabel = text;
    }

    // Determine label width based on text length
    let labelWidth = '200px';
    const textLength = text?.length || 0;
    if (textLength > 80) {
      labelWidth = '500px';
    } else if (textLength > 60) {
      labelWidth = '400px';
    } else if (textLength > 40) {
      labelWidth = '300px';
    } else if (textLength > 20) {
      labelWidth = '250px';
    }

    // Determine container style
    const containerStyle = (text?.includes('Replace') && inputType !== 'none') ? 'withoutFlex' : 'normal';

    // Determine permission key based on content
    let permissionKey = 'otherAllergenDeclarations';
    if (text?.includes('made with')) {
      permissionKey = 'thisProductMadeWith';
    }

    return {
      id: item?.id || '',
      name: String(item?.name ?? item?.id ?? ''), // Name sent instead of id (same as Packaging MultiInputDropdown)
      label: text,
      displayLabel,
      labelWidth,
      containerStyle,
      permissionKey,
      inputType,
      secondLabel,
      category: text?.includes('Artwork Team') ? 'artwork_team' : 
               text?.includes('made with') ? 'product_info' : 'other'
    };
  });
};

// Generate allergen declaration options from static text list (for backward compatibility)
export const generateAllergenDeclarationOptions = () => {
  if (!ALLERGEN_DECLARATION_TEXT_LIST || !Array.isArray(ALLERGEN_DECLARATION_TEXT_LIST)) {
    return [];
  }

  return ALLERGEN_DECLARATION_TEXT_LIST.filter(text => text && typeof text === 'string').map((text, index) => {
    // Generate unique ID from text
    const id = text
      .replace(/[^a-zA-Z0-9\s]/g, '') // Remove special characters
      .replace(/\s+/g, '_') // Replace spaces with underscores
      .toUpperCase();

    // Determine input type based on text content
    let inputType = 'single';
    let displayLabel = text;
    let secondLabel = '';

    if (text?.includes('{} with {}')) {
      inputType = 'double';
      displayLabel = text.replace(' {} with {}', '');
      secondLabel = 'With';
    } else if (text?.includes('{}')) {
      inputType = 'single';
      displayLabel = text.replace(' {}', '');
    } else if (text?.includes('variance approved') || !text?.includes('{}')) {
      inputType = 'none';
      displayLabel = text;
    }

    // Determine label width based on text length
    let labelWidth = '200px';
    const textLength = text?.length || 0;
    if (textLength > 80) {
      labelWidth = '500px';
    } else if (textLength > 60) {
      labelWidth = '400px';
    } else if (textLength > 40) {
      labelWidth = '300px';
    } else if (textLength > 20) {
      labelWidth = '250px';
    }

    // Determine container style
    const containerStyle = (text?.includes('Replace') && inputType !== 'none') ? 'withoutFlex' : 'normal';

    // Determine permission key based on content
    let permissionKey = 'otherAllergenDeclarations';
    if (text?.includes('made with')) {
      permissionKey = 'thisProductMadeWith';
    }

    return {
      id,
      label: text,
      displayLabel,
      labelWidth,
      containerStyle,
      permissionKey,
      inputType,
      secondLabel,
      category: text?.includes('Artwork Team') ? 'artwork_team' : 
               text?.includes('made with') ? 'product_info' : 'other'
    };
  });
};

// Pre-generated options for performance (computed once)
export const ALLERGEN_DECLARATION_OPTIONS = generateAllergenDeclarationOptions();
